import { useRouter } from "next/router";
import shallow from "zustand/shallow";
import useCompanySearchStore from "stores/use-company-search-store";
import useUser from "hooks/use-user";
import type { TagLinkProps } from "components/common/link/tag-link";
import TagLink from "components/common/link/tag-link";
import { getSelectedIndustry, useIndustries } from "utils/industries";
import colorSchemes from "data/color-schemes";

interface IndustryTagProps extends TagLinkProps {
  industry: string;
  industryId?: number;
  shouldIncludeLink?: boolean;
}

const IndustryTag = ({
  industry,
  industryId,
  shouldIncludeLink = false,
  ...props
}: IndustryTagProps) => {
  const router = useRouter();
  const user = useUser();

  const { resetCompanyFilters, updateCompanyFilter, getCompanySearch } =
    useCompanySearchStore(
      (store) => ({
        resetCompanyFilters: store.resetFilters,
        updateCompanyFilter: store.updateFilter,
        getCompanySearch: store.getSearch,
      }),
      shallow
    );

  // Navigate to industry search on click and set that value in the search
  const industries = useIndustries();
  const redirectUrl = "/search/company";
  const isFreeUser = user?.access?.group === "free";
  const isPublicUser = !user?.isLoggedIn;

  const handleIndustryClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (isPublicUser) {
      router.push(`/signup?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }

    if (isFreeUser) {
      router.push("/upgrade");
      return;
    }

    resetCompanyFilters();
    const newSelectedCompanyIndustries = getSelectedIndustry(
      industry,
      industries
    );

    updateCompanyFilter("selectedIndustries", newSelectedCompanyIndustries);
    getCompanySearch();
    router.push(redirectUrl);
  };

const willIncludeLink: boolean =
  shouldIncludeLink ||
  !!industryId ||
  (Array.isArray(industries) && industries.length > 0);

  const href = isPublicUser
    ? `/signup?redirect=${encodeURIComponent(redirectUrl)}`
    : redirectUrl;

  return (
    <TagLink
      {...props}
      label={industry}
      colorScheme={colorSchemes.industry}
      href={willIncludeLink ? href : undefined}
      onClick={willIncludeLink ? handleIndustryClick : undefined}
      title={industry}
    />
  );
};

export default IndustryTag;
